// 装備（ハクスラ式）の表示の部品：絵・詳細の札・合成・一括売却。編成（付け替え）と強化（強化・合成）の両方で使う。
// 詳細の札は mode で操作を変える：loadout＝装備・外す・売却（強化は強化の画面へ）、forge＝強化・合成・売却
import {
  AFFIX_POOL,
  CHARS,
  CHAR_IDS,
  EQUIPMENT,
  EQUIP_FLAG_TEXT,
  EQUIP_SET_BY_ID,
  JUTSU_BY_ID,
  META_CONFIG,
  affixRange,
  affixText,
  enhanceCost,
  equipName,
  equippedItems,
  isEquipped,
  itemStats,
  itemTraitEffects,
  itemTraitFlags,
  levelCap,
  limitBreakCost,
  limitBreakFodder,
  selectedJutsu,
  sellValue,
  setBonuses,
  smithLbMax,
  smithLevelForLb,
  traitText,
  type Affix,
  type AutoSell,
  type CharId,
  type EquipItem,
  type EquipSlot,
  type Profile,
  type Rarity,
  type SellFilter,
} from '@hyakki/core';
import { api } from '../api';
import { enemyIconHtml, faceStyle, itemFrameCls, itemIconStyle } from '../assets';
import { act, state } from '../state';
import { costHtml, esc, fmt } from './dom';
import { icon } from './icons';
import { confirmDialog, openModal, toast } from './overlay';
import { errorText, runOp as run } from './status';
import { lvBoxHtml, type LvRow } from './lvup';
import { lvConfirm } from './shiki';
import { powerOf } from '../core-bridge';

export const SLOT_LABEL: Record<EquipSlot, string> = { weapon: '武器', armor: '防具', netsuke: 'アクセサリー' };
export const SLOT_ICON: Record<EquipSlot, string> = { weapon: 'attack', armor: 'shield', netsuke: 'omamori' };
/** 並・良・優・極 */
export const RARITY: Record<string, { label: string; n: number }> = { n: { label: '並', n: 1 }, r: { label: '良', n: 2 }, sr: { label: '優', n: 3 }, ssr: { label: '極', n: 4 } };
const STAT_LABEL: Record<string, string> = { hp: 'HP', atk: '攻撃', def: '防御', agi: '速さ' };
export const AUTO_LABEL: Record<AutoSell, string> = { none: 'しない', n: '並のみ', r: '良以下' };

/** 装備の名前（業物はシリーズ名つき、神品は【神品】） */
export const itemName = (it: EquipItem) => equipName(it);
/** 追加効果（古い形 mei にも対応） */
export const affixesOf = (it: EquipItem): Affix[] => (Array.isArray(it.affixes) ? it.affixes : it.mei ? [{ id: it.mei.id, value: it.mei.value, q: 0.5 }] : []);
export const equippedBy = (p: Profile, id: number): CharId | null => (CHAR_IDS.find((c) => Object.values(p.sets[c] ?? {}).includes(id)) as CharId | undefined) ?? null;
export const rarOf = (r: string) => RARITY[r] ?? RARITY.n;
/** レア度・段・強化値・追加効果の数の順（強い順） */
export const byRare = (a: EquipItem, b: EquipItem) => rarOf(b.rarity).n - rarOf(a.rarity).n || b.tier - a.tier || b.level - a.level || affixesOf(b).length - affixesOf(a).length;

/** 装備の絵。妖の装備は元の妖の顔を小さく重ねる、名物は金の縁 */
export const itemThumb = (it: EquipItem, cls = '') => {
  const def = EQUIPMENT[it.defId];
  return `<span class="thumb ${itemFrameCls(it.defId, it.rarity)} ${cls}" style="${itemIconStyle(it.defId)}"><em class="rar ${it.rarity}">${rarOf(it.rarity).label}</em>${it.level ? `<small>+${it.level}</small>` : ''}${def?.enemyId ? `<i class="it-foe">${enemyIconHtml(def.enemyId, 22)}</i>` : ''}${it.locked ? `<i class="it-lock">${icon('lock')}</i>` : ''}</span>`;
};

/** 一覧の1マス（付けているキャラの顔・追加効果の数の点） */
export function itemCellHtml(p: Profile, i: EquipItem, opts: { sel?: boolean; other?: boolean; tag?: string } = {}) {
  const by = equippedBy(p, i.id);
  const n = affixesOf(i).length;
  return `<li><button class="inv-cell ${opts.sel ? 'sel' : ''} ${opts.other ? 'other' : ''}" data-item="${i.id}" title="${esc(itemName(i))}">${itemThumb(i)}
    ${by ? `<span class="by-face" style="${faceStyle(by)}" aria-label="${esc(CHARS[by].name)}が装備中"></span>` : ''}${n ? `<span class="af-dots" aria-label="追加効果${n}">${'<i></i>'.repeat(n)}</span>` : ''}${opts.tag ?? ''}</button></li>`;
}

/** 追加効果の値（affixText と同じ書き方：% は小数1桁） */
const affixNum = (id: Affix['id'], v: number) => (AFFIX_POOL[id].unit === '%' ? `${Math.round(v * 1000) / 10}%` : String(v));
/** この段での値の幅（例「3.4〜9.0%」）。固定値の効果は幅なし */
function rangeText(a: Affix, tier: number) {
  const r = affixRange(a.id, tier);
  if (AFFIX_POOL[a.id].fixed) return '固定';
  if (r.lo === r.hi) return affixNum(a.id, r.lo);
  return `${affixNum(a.id, r.lo)}〜${affixNum(a.id, r.hi)}`;
}

function affixesHtml(it: EquipItem) {
  const afs = affixesOf(it);
  return `<div class="affixes" aria-label="追加効果">${
    afs.length
      ? afs
          .map(
            (a) => `<div class="af-row ${AFFIX_POOL[a.id]?.drawback ? 'drawback' : ''}">
              <span class="af-main"><span class="af-txt">${esc(AFFIX_POOL[a.id] ? affixText(a) : `${a.id} +${a.value}`)}</span></span><i class="af-q" role="img" aria-label="品質 ${Math.round(a.q * 100)}%${AFFIX_POOL[a.id] ? `（段${it.tier}の幅 ${rangeText(a, it.tier)}）` : ''}" title="品質 ${Math.round(a.q * 100)}%${AFFIX_POOL[a.id] ? `（段${it.tier}の幅 ${rangeText(a, it.tier)}）` : ''}"><i style="width:${Math.round(Math.max(0.04, a.q) * 100)}%"></i></i></div>`,
          )
          .join('')
      : '<p class="muted small af-none">追加効果なし</p>'
  }</div>`;
}

/** 戦い方（系統のフラグ）と相性の術 */
function styleHtml(it: EquipItem, syn: boolean): string {
  const def = EQUIPMENT[it.defId];
  const flags = itemTraitFlags(it).filter((f) => EQUIP_FLAG_TEXT[f]);
  const j = def?.jutsu ? JUTSU_BY_ID[def.jutsu] : undefined;
  if (!flags.length && !j) return '';
  return `<p class="it-style">${flags.map((f) => `<span><em class="tag sty">戦法</em>${esc(EQUIP_FLAG_TEXT[f])}</span>`).join('')}${
    j ? `<span class="syn ${syn ? 'on' : ''}"><em class="tag">相性</em>術「${esc(j.name)}」で固有×${META_CONFIG.equipment.synergyMult}</span>` : ''
  }</p>`;
}

/** 業物（セット）：シリーズ名と、2部位・3部位の効果（そのキャラで今そろっている数で光る） */
function setInfoHtml(p: Profile, it: EquipItem, charId: CharId): string {
  const def = it.set ? EQUIP_SET_BY_ID[it.set] : undefined;
  if (!def) return '';
  const n = setBonuses(equippedItems(p, charId)).find((x) => x.def.id === def.id)?.count ?? 0;
  return `<p class="it-set"><em class="tag set">業物「${esc(def.name)}」</em><span class="${n >= 2 ? 'on' : ''}">2部位：${esc(def.text2)}</span><span class="${n >= 3 ? 'on' : ''}">3部位：${esc(def.text3)}</span><small>${n}/3</small></p>`;
}

/**
 * 詳細の札。charId は付け替えるキャラ（forge では付けているキャラか出撃キャラ）。
 * 操作は data-a：equip・unequip・enhance・enhance10・merge・lock・sell・forge（強化の画面へ）・close
 */
export function itemDetailHtml(p: Profile, it: EquipItem, charId: CharId, mode: 'loadout' | 'forge') {
  const forOther = !!it.charId && it.charId !== charId;
  const by = equippedBy(p, it.id);
  const onThis = by === charId;
  const atCap = it.level >= levelCap(it);
  const stats = Object.entries(itemStats(it)).filter(([, v]) => v);
  const zeni = state.resources.zeni ?? 0;
  const sellable = !it.locked && !isEquipped(p, it.id);
  // 相性の術を持っていくと固有効果が上がる（武器の持ち主のキャラで見る）
  const owner = it.charId ?? by ?? charId;
  const syn = !!EQUIPMENT[it.defId]?.jutsu && EQUIPMENT[it.defId].jutsu === selectedJutsu(p, owner);
  const rar = rarOf(it.rarity);
  const sell = `<button class="btn danger" data-a="sell" ${sellable ? '' : `disabled title="${it.locked ? 'ロック中' : '装備中'}：売却・一括売却の対象外"`}><span>売却</span><small>${costHtml({ zeni: sellValue(it) })}</small></button>`;
  const grow =
    atCap && it.lb < META_CONFIG.equipment.lbMax
      ? it.lb < smithLbMax(p)
        ? `<button class="btn primary merge-btn" data-a="merge"><span>${icon('plusbox')}限界突破</span><small>上限+10・${costHtml({ zeni: limitBreakCost(it) })}</small></button>`
        : `<button class="btn primary merge-btn" disabled><span>${icon('lock')}限界突破</span><small>里の鍛冶屋 Lv.${smithLevelForLb(it.lb)}</small></button>`
      : `<button class="btn primary" data-a="enhance" ${atCap || zeni < enhanceCost(it) ? 'disabled' : ''}><span>${icon('enhance')}強化</span><small>${atCap ? '最大' : costHtml({ zeni: enhanceCost(it) })}</small></button>
      <button class="btn" data-a="enhance10" ${atCap || zeni < enhanceCost(it) ? 'disabled' : ''}><span>${icon('speed')}まとめて強化</span></button>`;
  const actions =
    mode === 'forge'
      ? `${grow}${sell}`
      : `${onThis ? `<button class="btn primary" data-a="unequip">外す</button>` : `<button class="btn primary" data-a="equip" ${forOther ? 'disabled' : ''}>装備</button>`}
        <button class="btn" data-a="forge"><span>${icon('enhance')}強化</span><small>+${it.level}/${levelCap(it)}</small></button>${sell}`;
  return `<div class="item-card rar-${it.rarity}">
    <header class="ed-head">${itemThumb(it, 'big')}<div class="ed-name"><h2>${esc(itemName(it))}</h2>
      <small class="muted"><em class="rar ${it.rarity}">${rar.label}</em> ${SLOT_LABEL[it.slot]}・段${it.tier}・強化${it.level}/${levelCap(it)}${it.lb ? `・突破${it.lb}` : ''}</small></div>
      <button class="icon-btn lock-btn ${it.locked ? 'on' : ''}" data-a="lock" aria-pressed="${!!it.locked}" aria-label="${it.locked ? 'ロックを外す' : 'ロックする'}" title="${it.locked ? 'ロック中（押すと外す）' : 'ロックしていない（押すとロック）'}">${icon(it.locked ? 'lock' : 'unlock')}</button>
      <button class="icon-btn ed-close" data-a="close" aria-label="閉じる">${icon('close')}</button></header>
    <div class="mini-kv">${stats.map(([k, v]) => `<span>${STAT_LABEL[k] ?? k}<b>+${Math.round(v * 10) / 10}</b></span>`).join('')}</div>
    <p class="it-trait">${it.shin ? '<em class="tag shin">神品</em>' : ''}${EQUIPMENT[it.defId]?.meibutsu ? '<em class="tag mb">名物</em>' : '<em class="tag">固有</em>'}${esc(itemTraitEffects(it).map((e) => traitText(syn ? { ...e, value: Math.round(e.value * META_CONFIG.equipment.synergyMult * 1000) / 1000 } : e)).join('・'))}</p>
    ${styleHtml(it, syn)}
    ${setInfoHtml(p, it, owner)}
    ${affixesHtml(it)}
    ${forOther && mode === 'loadout' ? `<p class="ed-req">${CHARS[it.charId!].name}専用の武器です</p>` : ''}
    ${by && !(onThis && mode === 'loadout') ? `<p class="ed-note"><span class="by-face inline" style="${faceStyle(by)}"></span>${CHARS[by].name}が装備中</p>` : ''}
    ${mode === 'forge' && !atCap ? lvBoxHtml('', enhanceRows(p, it, 1, owner)) : ''}
    <div class="ed-actions item-actions">${actions}</div></div>`;
}

/** まとめて強化：次の壁（+10ごと）か、銭の続くところまで。強化する回数と銭の合計 */
export function bulkEnhancePlan(it: EquipItem, zeni: number): { n: number; cost: number } {
  const stop = Math.min(levelCap(it), (Math.floor(it.level / 10) + 1) * 10);
  let n = 0;
  let cost = 0;
  for (let lv = it.level; lv < stop; lv++) {
    const c = enhanceCost({ ...it, level: lv });
    if (cost + c > zeni) break;
    cost += c;
    n++;
  }
  return { n, cost };
}
/** 強化 n 回で変わるもの：能力値と、付けているキャラの戦力 */
function enhanceRows(p: Profile, it: EquipItem, n: number, charId: CharId): LvRow[] {
  const up = { ...it, level: it.level + n };
  const a = itemStats(it);
  const b = itemStats(up);
  const r1 = (v: number) => String(Math.round(v * 10) / 10);
  const rows: LvRow[] = (Object.keys(b) as (keyof typeof b)[]).filter((k) => a[k] || b[k]).map((k) => ({ label: STAT_LABEL[k] ?? k, before: `+${r1(a[k])}`, after: `+${r1(b[k])}` }));
  const by = equippedBy(p, it.id);
  if (by) {
    const q = { ...p, items: p.items.map((x) => (x.id === it.id ? up : x)) } as Profile;
    const before = powerOf(p, by);
    const after = powerOf(q, by);
    if (before != null && after != null) rows.push({ label: `${CHARS[by].name}の戦力`, before: fmt(before), after: fmt(after) });
  }
  void charId;
  return rows;
}

/**
 * 詳細の札の操作（data-a）。済んだら true（画面を描き直す）。forge（強化の画面へ）は呼び出し側で扱う
 */
export async function runItemAction(a: string, p: Profile, it: EquipItem, charId: CharId): Promise<boolean> {
  switch (a) {
    case 'equip':
      return run(api.equip(charId, it.slot, it.id), '装備しました', 'equip');
    case 'unequip':
      return run(api.equip(charId, it.slot, null));
    case 'enhance':
      return run(api.enhance(it.id), '強化しました', 'enhance');
    case 'enhance10': {
      // まとめて：次の壁（+10ごと）か銭の続くところまで。いくらで何がどれだけ上がるかを見せてから
      const plan = bulkEnhancePlan(it, state.resources.zeni ?? 0);
      if (!plan.n) return false;
      const ok = await lvConfirm(`まとめて強化：${itemName(it)}`, `${costHtml({ zeni: plan.cost })}`, lvBoxHtml(`強化${it.level} → ${it.level + plan.n}`, enhanceRows(p, it, plan.n, charId)));
      if (!ok) return false;
      return run(api.enhance(it.id, plan.n), '強化しました', 'enhance');
    }
    case 'merge':
      mergeDialog(p, it);
      return false;
    case 'lock':
      return run(api.lock(it.id, !it.locked), it.locked ? 'ロックを外しました' : 'ロックしました');
    case 'sell':
      if (!(await confirmDialog(`${itemName(it)} を売却しますか？\n銭 ${fmt(sellValue(it))} を受け取ります。`, { danger: true, ok: '売却' }))) return false;
      return run(api.sell([it.id]), '売却しました', 'coin');
  }
  return false;
}

/** 装備の性能だけを見る札（戦果画面のドロップなど）。強化・装備などの操作は無し */
export function itemInfoDialog(it: EquipItem, note = '') {
  const p = state.profile;
  const stats = Object.entries(itemStats(it)).filter(([, v]) => v);
  const rar = rarOf(it.rarity);
  const m = openModal(
    `<div class="item-card item-info rar-${it.rarity}">
    <header class="ed-head">${itemThumb(it, 'big')}<div class="ed-name"><h2>${esc(itemName(it))}</h2>
      <small class="muted"><em class="rar ${it.rarity}">${rar.label}</em> ${SLOT_LABEL[it.slot]}・段${it.tier}${it.charId ? `・${CHARS[it.charId].name}専用` : ''}</small></div>
      <button class="icon-btn ed-close" data-close aria-label="閉じる">${icon('close')}</button></header>
    <div class="mini-kv">${stats.map(([k, v]) => `<span>${STAT_LABEL[k] ?? k}<b>+${Math.round(v * 10) / 10}</b></span>`).join('')}</div>
    <p class="it-trait">${it.shin ? '<em class="tag shin">神品</em>' : ''}${EQUIPMENT[it.defId]?.meibutsu ? '<em class="tag mb">名物</em>' : '<em class="tag">固有</em>'}${esc(itemTraitEffects(it).map((e) => traitText(e)).join('・'))}</p>
    ${styleHtml(it, false)}
    ${p ? setInfoHtml(p, it, it.charId ?? state.oshi) : ''}
    ${affixesHtml(it)}
    ${note ? `<p class="ed-note">${esc(note)}</p>` : ''}</div>`,
    { className: 'sheet-wrap' },
  );
  m.body.querySelector('.ed-close')?.addEventListener('click', () => m.close());
}

/** 限界突破：素材（同じ装備でレア度が同じか上。ロック中・装備中は除く）を選ぶ → 確認して合成 */
export function mergeDialog(p: Profile, it: EquipItem) {
  const list = limitBreakFodder(p, it.id);
  const m = openModal(
    list.length
      ? `<p class="muted small">素材は消えます（強化・追加効果も）。同じ「${esc(itemName(it))}」で、レア度が同じか上のものを選んでください。</p>
        <ul class="merge-list fit-scroll">${list
          .map((x) => `<li><button class="merge-row" data-fodder="${x.id}">${itemThumb(x)}<span class="grow"><b>${esc(itemName(x))}</b><small><em class="rar ${x.rarity}">${rarOf(x.rarity).label}</em>・段${x.tier}・強化${x.level}${(x.affixes?.length ?? 0) ? `・追加効果${x.affixes.length}` : ''}</small></span>${icon('right', 'chev')}</button></li>`)
          .join('')}</ul>`
      : `<p class="muted">素材にできる装備がありません（同じ「${esc(itemName(it))}」でレア度が同じか上のもの。ロック中と装備中を除く）。</p>`,
    { title: `限界突破：${itemName(it)}（上限 +${levelCap(it)} → +${levelCap(it) + 10}）` },
  );
  m.body.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-fodder]');
    if (!b) return;
    const f = list.find((x) => x.id === Number(b.dataset.fodder));
    if (!f) return;
    if (!(await confirmDialog(`${itemName(f)} を素材にして限界突破します。\n素材は消えます。費用：銭 ${fmt(limitBreakCost(it))}`, { ok: '限界突破' }))) return;
    m.close();
    await run(api.limitBreak(it.id, f.id), `限界突破しました（上限 +${levelCap(it) + 10}）`, 'enhanceBig');
  });
}

/** 段の欄（k：tier＝一括売却、autotier＝自動売却）。0 は「すべて」 */
const tierPick = (k: string, v: number) =>
  `<div class="tier-pick" data-k="${k}" data-cur="${v}"><button class="chip ${v ? '' : 'on'}" data-tv="all">すべて</button><button class="chip tp-step" data-tv="minus" aria-label="1段下げる">−</button><b class="tp-v ${v ? 'on' : ''}">${v ? `${v}段以下` : '—'}</b><button class="chip tp-step" data-tv="plus" aria-label="1段上げる" ${v >= 10 ? 'disabled' : ''}>＋</button></div>`;

/** 一括売却：条件を選ぶと件数と銭の合計 → 確認して売却。自動売却の設定もここ */
export function sellDialog(p: Profile) {
  const f: SellFilter = { maxRarity: 'n', maxTier: undefined, maxAffixes: undefined };
  const RAR_OPTS: Rarity[] = ['n', 'r', 'sr'];
  const m = openModal(
    `<div class="sell-form">
      <p class="muted small">ロック中・装備中の装備は対象外です。売却で得られるのは銭のみです。</p>
      <div class="sf-row"><span>レア度</span><div class="seg" data-k="rar">${RAR_OPTS.map((r) => `<button class="chip ${r === f.maxRarity ? 'on' : ''}" data-v="${r}">${rarOf(r).label}${r === 'n' ? 'のみ' : '以下'}</button>`).join('')}</div></div>
      <div class="sf-row"><span>段</span>${tierPick('tier', 0)}</div>
      <div class="sf-row"><span>追加効果</span><div class="seg" data-k="af">${[-1, 0, 1, 2].map((t) => `<button class="chip ${t === -1 ? 'on' : ''}" data-v="${t}">${t < 0 ? 'すべて' : `${t}個以下`}</button>`).join('')}</div></div>
      <div class="sf-preview" aria-live="polite"></div>
      <div class="btn-row"><button class="btn" data-close>やめる</button><button class="btn danger" data-go disabled>売却する</button></div>
      <hr class="rule">
      <div class="sf-row"><span>自動売却<small class="muted">（拾った時に銭へ）</small></span><div class="seg" data-k="auto">${(['none', 'n', 'r'] as AutoSell[])
        .map((v) => `<button class="chip ${(p.settings?.autoSell ?? 'none') === v ? 'on' : ''}" data-v="${v}">${AUTO_LABEL[v]}</button>`)
        .join('')}</div></div>
      <div class="sf-row sf-auto-tier"><span>段</span>${tierPick('autotier', p.settings?.autoSellTier ?? 0)}</div>
    </div>`,
    { title: '一括売却' },
  );
  const out = m.body.querySelector<HTMLElement>('.sf-preview')!;
  const goBtn = m.body.querySelector<HTMLButtonElement>('[data-go]')!;
  let seq = 0;
  /** サーバーの見積もり（正本）。取れなかったときは「計算できません」で売却ボタンも止める */
  let est: { count: number; zeni: number } | null = null;
  const preview = async () => {
    const my = ++seq;
    est = null;
    goBtn.disabled = true;
    out.innerHTML = '<span class="muted">計算中…</span>';
    try {
      const r = await api.sellPreview({ ...f });
      if (my !== seq) return;
      est = r;
      out.innerHTML = `<b>${r.count}</b>個・${costHtml({ zeni: r.zeni })}`;
      goBtn.disabled = !r.count;
    } catch {
      if (my !== seq) return;
      out.innerHTML = '<span class="bad">計算できません</span>';
    }
  };
  void preview();
  let autoTier = p.settings?.autoSellTier ?? 0;
  /** 段の欄：すべて／−・N段以下・＋（1段ずつ） */
  const setTier = (k: string, v: number) => {
    const box = m.body.querySelector<HTMLElement>(`[data-k="${k}"]`)!;
    box.outerHTML = tierPick(k, v);
  };
  m.body.addEventListener('click', async (e) => {
    const tb = (e.target as HTMLElement).closest<HTMLButtonElement>('.tier-pick [data-tv]');
    if (tb) {
      const k = tb.closest<HTMLElement>('[data-k]')!.dataset.k!;
      const cur = Number(tb.closest<HTMLElement>('[data-k]')!.dataset.cur) || 0;
      const v = tb.dataset.tv === 'all' ? 0 : tb.dataset.tv === 'minus' ? Math.max(1, (cur || 11) - 1) : Math.min(10, (cur || 0) + 1);
      if (k === 'tier') {
        f.maxTier = v || undefined;
        setTier(k, v);
        return void preview();
      }
      tb.disabled = true;
      if (await run(api.setAutoSell((p.settings?.autoSell ?? 'none') as AutoSell, v || null), `自動売却：${v ? `${v}段以下` : '段は問わない'}`)) {
        autoTier = v;
        setTier(k, v);
      } else tb.disabled = false;
      return;
    }
    const c = (e.target as HTMLElement).closest<HTMLButtonElement>('.seg .chip');
    if (c) {
      const k = c.closest<HTMLElement>('[data-k]')!.dataset.k;
      const v = c.dataset.v!;
      if (k === 'auto') {
        c.disabled = true;
        if (await run(api.setAutoSell(v as AutoSell, autoTier || null), `自動売却：${AUTO_LABEL[v as AutoSell]}`)) c.parentElement!.querySelectorAll('.chip').forEach((x) => x.classList.toggle('on', x === c));
        c.disabled = false;
        return;
      }
      c.parentElement!.querySelectorAll('.chip').forEach((x) => x.classList.toggle('on', x === c));
      if (k === 'rar') f.maxRarity = v as Rarity;
      if (k === 'tier') f.maxTier = Number(v) || undefined;
      if (k === 'af') f.maxAffixes = Number(v) < 0 ? undefined : Number(v);
      return void preview();
    }
    if ((e.target as HTMLElement).closest('[data-go]')) {
      if (!est?.count) return;
      if (!(await confirmDialog(`条件に合う装備 ${est.count}個 を売却します。\n銭 ${fmt(est.zeni)} を受け取ります。元に戻せません。`, { ok: '売却する', danger: true }))) return;
      goBtn.disabled = true;
      try {
        const r = await act(api.sellFilter({ ...f }));
        toast(`${r.count}個を売却しました（銭 +${fmt(r.zeni)}）`, 'good');
        m.close();
      } catch (err) {
        toast(errorText(err), 'error');
        void preview();
      }
    }
  });
}
